/**
 * useLorPreview
 *
 * Encapsulates preview fetching for a generated document and lifecycle
 * management of the created object URL. Consumers control when to request
 * the preview by setting the documentId.
 */
import { skipToken } from '@reduxjs/toolkit/query';
import { useCallback, useEffect, useState } from 'react';

import { useGetMatterDocumentPreviewQuery } from '@/services/api/mattersService';

/**
 * useLorPreview
 *
 * Fetches and manages a preview URL for a Letter of Representation (LoR)
 * document. Internally requests the preview blob from the backend and
 * exposes a temporary object URL that is automatically revoked on change
 * or unmount.
 *
 * - Automatically toggles a lightweight loading state while the preview
 *   request is in-flight or a new URL is being created.
 * - Provides a `request` helper to manually re-request a preview when the
 *   selected document changes externally.
 *
 * @param matterId The matter identifier the document belongs to
 * @param documentId The selected document id to preview, or null when none
 * @returns Immutable object containing:
 * - `url`: object URL for the preview PDF, or null when not available
 * - `isLoading`: true while the preview is being fetched/prepared
 * - `error`: any request error from the preview API
 * - `request`: function to trigger a new preview for a given document id
 */
export const useLorPreview = (matterId: string, documentId: string | null) => {
  const [isRequested, setIsRequested] = useState(false);
  const [url, setUrl] = useState<string | null>(null);
  const [nonce, setNonce] = useState<number | null>(null);
  const [filename, setFilename] = useState<string>(
    'Letter-of-Representation.pdf',
  );
  const {
    data: previewData,
    isFetching,
    error,
  } = useGetMatterDocumentPreviewQuery(
    documentId
      ? { matter_id: matterId, document_id: documentId, t: nonce ?? undefined }
      : skipToken,
  );

  useEffect(() => {
    if (!previewData) {
      return () => {};
    }
    // Debug: log filename and size
    // eslint-disable-next-line no-console
    console.log('Preview fetched', {
      t: nonce,
      filename: previewData.filename,
      size: previewData.blob?.size,
      type: previewData.blob?.type,
    });
    const nextUrl = URL.createObjectURL(previewData.blob);
    setUrl(nextUrl);
    setFilename(previewData.filename || 'Letter-of-Representation.pdf');
    setIsRequested(false);
    return () => {
      URL.revokeObjectURL(nextUrl);
    };
  }, [previewData]);

  useEffect(() => {
    if (error) setIsRequested(false);
  }, [error]);

  const request = useCallback(() => {
    setUrl((currentUrl) => {
      if (currentUrl) {
        URL.revokeObjectURL(currentUrl);
      }
      return null;
    });
    setIsRequested(true);
    setNonce(Date.now());
  }, []);

  const isLoading = isFetching || (isRequested && !error && !url);

  return { url, filename, isLoading, error, request } as const;
};
